import type { Ref } from 'vue'
import { nextTick, unref } from 'vue'
import {
  commentTreeHasId,
  getAnchorNodeByHash,
  replaceRouteHash,
  resolveAnchorIdFromHash,
  resolveCommentIdFromHash,
  resolveInPageAnchorHrefFromClick,
  scrollToElementWithOffset,
  waitForAnchorNodeByHash
} from '~/composables/useAnchorScroll'

type ValueSource<T> = T | Ref<T> | (() => T)

type CommentAnchorNode = {
  ID?: number
  children?: CommentAnchorNode[]
}

type CommentAnchorScrollOptions<T extends CommentAnchorNode> = {
  comments: Ref<T[]>
  contentId: ValueSource<number | undefined>
  commentPageSize: ValueSource<number>
  isCommentPagerMode: ValueSource<boolean>
  currentCommentPage: ValueSource<number>
  commentNoMore: Ref<boolean>
  loadMoreComments: () => Promise<void>
}

const readValue = <T>(source: ValueSource<T>) => {
  if (typeof source === 'function') {
    return (source as () => T)()
  }
  return unref(source)
}

const waitForNextFrame = () => new Promise<void>(resolve => window.requestAnimationFrame(() => resolve()))

export const useCommentAnchorScroll = <T extends CommentAnchorNode>(options: CommentAnchorScrollOptions<T>) => {
  const route = useRoute()
  const router = useRouter()
  const blogApi = useBlogApi()
  let commentAnchorResolving = false

  const scrollToHashAnchor = (hash: string, behavior: ScrollBehavior = 'smooth') => {
    const node = getAnchorNodeByHash(hash)
    if (!node) {
      return false
    }
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    return scrollToElementWithOffset(node, prefersReducedMotion ? 'auto' : behavior)
  }

  const hasCommentLoaded = (targetId: number) => commentTreeHasId(options.comments.value, targetId)

  const ensureCommentAnchorVisible = async (hash: string, behavior: ScrollBehavior = 'smooth') => {
    if (!import.meta.client) {
      return
    }
    const targetId = resolveCommentIdFromHash(hash)
    if (targetId <= 0) {
      return
    }
    if (getAnchorNodeByHash(hash)) {
      scrollToHashAnchor(hash, behavior)
      return
    }
    const contentID = Number(readValue(options.contentId) || 0)
    if (commentAnchorResolving || contentID <= 0) {
      return
    }

    commentAnchorResolving = true
    try {
      const anchor = await blogApi.getCommentAnchor(contentID, targetId, readValue(options.commentPageSize))
      if (!anchor?.found) {
        return
      }

      if (readValue(options.isCommentPagerMode)) {
        if (anchor.page > 0 && anchor.page !== readValue(options.currentCommentPage)) {
          await router.replace({
            path: route.path,
            query: { ...route.query, cpage: String(anchor.page) },
            hash
          })
        }
        if (await waitForAnchorNodeByHash(hash)) {
          scrollToHashAnchor(hash, behavior)
        }
        return
      }

      const requiredRoots = Math.max(1, Number(anchor.rootIndex || 0) + 1)
      while (options.comments.value.length < requiredRoots && !options.commentNoMore.value) {
        await options.loadMoreComments()
      }
      while (!hasCommentLoaded(targetId) && !options.commentNoMore.value) {
        await options.loadMoreComments()
      }
      if (await waitForAnchorNodeByHash(hash)) {
        scrollToHashAnchor(hash, behavior)
      }
    } finally {
      commentAnchorResolving = false
    }
  }

  const ensureRouteHashVisible = async (hash: string, behavior: ScrollBehavior = 'smooth') => {
    if (!import.meta.client || !hash) {
      return
    }
    if (resolveCommentIdFromHash(hash) > 0) {
      await ensureCommentAnchorVisible(hash, behavior)
      return
    }
    if (scrollToHashAnchor(hash, behavior)) {
      return
    }
    if (await waitForAnchorNodeByHash(hash)) {
      scrollToHashAnchor(hash, behavior)
    }
  }

  const settleRouteHashVisible = async (hash: string, behavior: ScrollBehavior = 'smooth') => {
    if (!import.meta.client || !hash) {
      return
    }
    await ensureRouteHashVisible(hash, behavior)
    if (!getAnchorNodeByHash(hash)) {
      return
    }
    if (behavior === 'smooth' && resolveCommentIdFromHash(hash) <= 0) {
      return
    }
    const settledBehavior: ScrollBehavior = behavior === 'smooth' ? 'auto' : behavior
    await nextTick()
    for (let i = 0; i < 3; i += 1) {
      await waitForNextFrame()
      scrollToHashAnchor(hash, settledBehavior)
    }
  }

  const onDocumentAnchorClick = (event: MouseEvent) => {
    const href = resolveInPageAnchorHrefFromClick(event)
    if (!href) {
      return
    }
    const id = resolveAnchorIdFromHash(href)
    if (!id) {
      return
    }
    const hash = `#${encodeURIComponent(id)}`
    event.preventDefault()
    if (route.hash === hash) {
      nextTick(() => {
        void settleRouteHashVisible(hash, 'smooth')
      })
      return
    }
    void replaceRouteHash(router, route, hash)
  }

  return {
    onDocumentAnchorClick,
    settleRouteHashVisible
  }
}
